import { dom, events } from '@omegagrid/core';
import { msg } from '@omegagrid/localize';
import { FormContainer } from "@omegagrid/form";
import rp, { AppComponent, Application, ParsedComponentId, GridEditor, EditorContainer } from '@reporting/rp-app-host';
import { AccountingGridEditorAdapter, JournalGridAdapter, AccessLogGridAdapter, LoginSettingsGridEditorAdapter,
	SsoSettingsGridEditorAdapter, AccountingGroupGridEditorAdapter, AccountingSectionGridEditorAdapter, LimitGridEditorAdapter, EmailLogGridAdapter,
	AccountingGroupTreeAdapter, AccountingGroupFormAdapter, DbSettingsGridEditorAdapter, ServerTreeAdapter,
	EmbedFormAdapter} from './adapters';
import { AccountingGroupTreeOptions } from './adapters';
import { AccountingGroup } from '@reporting/rp-types/core';
import { EmbedModule } from './types';

const createAccountingsGridEditor = (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new AccountingGridEditorAdapter();
	return component;
}

const createAccountingGroupsGridEditorContainer = async (app: Application, componentId: ParsedComponentId) => {
	const createAccountingGroupAdapter = (server: string | null = null) => {
		return new AccountingGroupTreeAdapter(server ? { s: server } : {}, { selectMode: 'single', autoClose: true, checkboxes: false, search: true }, true);
	};
	const container = dom.createElement<EditorContainer>('rp-editor-container', {
		items: [{
			id: 'server',
			width: 300,
			type: 'dropdown',
			caption: msg('Server'),
			adapter: new ServerTreeAdapter({ selectMode: 'single', autoClose: true, checkboxes: false }, true),
		}, {
			id: 'accountingGroup',
			width: 300,
			type: 'dropdown',
			caption: `${msg('Group')} / ${msg('Accounting')}`,
			adapter: createAccountingGroupAdapter(),
		}],
		createComponent: id => rp.host.createComponent(id),
		// triggered when toolbar dropdown values change
		updateComponentId: (values) => {
			let id = '';
			if (values['server']) id += `-s-${values['server']}`;
			if (values['accountingGroup']) id += `-${values['accountingGroup']}`;
			return `accounting-groups${id}`;
		}
	});

	container.addEventListener('change', (e: events.ChangeEvent) => {
		const server = e.value['server'];
		if (server === undefined) return;
		const updatedItems = [...container.items];
		updatedItems[1] = {
			...updatedItems[1],
			adapter: createAccountingGroupAdapter(server)
		};
		container.items = updatedItems;
	});

	return container;
}

const createAccountingGroupsGridEditor = (app: Application, componentId: ParsedComponentId) => {
	const containerParams: AccountingGroupTreeOptions = {};
	for (let i = 1; i + 1 < componentId.ids.length; i += 2) {
		containerParams[componentId.ids[i] as keyof AccountingGroupTreeOptions] = componentId.ids[i + 1];
	}
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new AccountingGroupGridEditorAdapter(containerParams);
	return component;
}

const createAccountingSectionsGridEditor = (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new AccountingSectionGridEditorAdapter();
	return component;
}

const createAccountingGroupForm = async (app: Application, componentId: ParsedComponentId) => {
	const group = await rp.ms('accounting').get<AccountingGroup>(`groups/${parseInt(componentId.ids[1])}`);
	if (!group) return Promise.reject(new Error('Accounting group not found'));
	const component = dom.createElement<FormContainer>('og-form-container');
	component.adapter = new AccountingGroupFormAdapter(group);
	// components.get() CREATES and registers the component when it is not open yet (and returns a
	// promise for it), so ask first - otherwise this leaves an orphan container behind and throws
	component.addEventListener('form.save', async () => {
		if (!rp.host.components?.has('accounting-groupsContainer')) return;
		(rp.host.components.get('accounting-groupsContainer') as EditorContainer)?.requestUpdate();
	});
	return component as unknown as AppComponent;
}

const createJournalGrid = async (app: Application, componentId: ParsedComponentId) => {
	// accounting-journal (group), accounting-journal-super (all), accounting-journal-compact (current company popup)
	const compact = componentId.ids[1] === 'compact';
	const idGroup = componentId.ids[1] !== 'super' ? rp.accountingProvider.accounting.id_accounting_group : null;
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new JournalGridAdapter(idGroup, compact);
	return component;
}

const createAccessLogGrid = async (app: Application, componentId: ParsedComponentId) => {
	const idGroup = componentId.ids[1] !== 'super' ? rp.accountingProvider.accounting.id_accounting_group : null;
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new AccessLogGridAdapter(idGroup);
	return component;
}

const createEmailLogGrid = async (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new EmailLogGridAdapter();
	return component;
}

const createLoginSettingsGridEditor = async (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new LoginSettingsGridEditorAdapter();
	return component;
}

const createSsoSettingsGridEditor = async (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new SsoSettingsGridEditorAdapter();
	return component;
}

const createLimitGridEditor = async (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new LimitGridEditorAdapter();
	return component;
}

const createDbSettingsGridEditor = async (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new DbSettingsGridEditorAdapter(parseInt(componentId.ids[1]));
	return component;
}

const createEmbedForm = async (app: Application, componentId: ParsedComponentId) => {
	const module = componentId.ids[1] as EmbedModule;
	const idReport = parseInt(componentId.ids[2]);
	const component = dom.createElement<FormContainer>('og-form-container');
	component.adapter = new EmbedFormAdapter(module, idReport);
	return component as unknown as AppComponent;
}

export const componentFactories = new Map<string, (app: Application, componentId: ParsedComponentId) => AppComponent|Promise<AppComponent>>([
	['accountings', createAccountingsGridEditor],
	['groups', createAccountingGroupsGridEditor],
	['groupsContainer', createAccountingGroupsGridEditorContainer],
	['sections', createAccountingSectionsGridEditor],
	['groupForm', createAccountingGroupForm],
	['limits', createLimitGridEditor],
	['journal', createJournalGrid],
	['accessLog', createAccessLogGrid],
	['emailLog', createEmailLogGrid],
	['loginSettings', createLoginSettingsGridEditor],
	['ssoSettings', createSsoSettingsGridEditor],
	['dbSettings', createDbSettingsGridEditor],
	['embedForm', createEmbedForm],
]);
